import React, { useEffect, useRef, useState } from 'react';
import { AvForm } from 'availity-reactstrap-validation';
import SaveSubmitButton from '../../form/buttons/SaveSubmitButton';
import {
  closeModalSafely,
  handleSubmit,
} from './create-or-edit-printer.service';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { fetchPrinters } from '../../../../redux/store/store.actions';
import FormField from '../../form/fields/FormField';
import {
  setIsCreatePrinterModalOpen,
  setIsEditPrinterModalOpen,
} from '../../../../redux/modal/modal.actions';
import ItemCategoryField from '../../form/fields/ItemCategoryField';
import { Col, Row, Button } from 'reactstrap';
import DropdownField from '../../form/fields/DropdownField';
import { basePostQuery } from '../../../../utility/sb-api/sb-api.service';
import {
  ORDER_TYPE_CATERING,
  ORDER_TYPE_DELIVERY,
  ORDER_TYPE_INHOUSE,
  ORDER_TYPE_PICKUP,
  ORDER_TYPE_SELF_SERVICE,
} from '../../../../utility/constants';

const CreateOrEditPrinterModal = ({
  isCreatePrinterModalOpen,
  isEditPrinterModalOpen,
  createPrinterModalData,
  editPrinterModalData,
  itemCategories,

  setIsCreatePrinterModalOpen,
  setIsEditPrinterModalOpen,
  fetchPrinters,
}) => {
  const intl = useIntl();
  const [targetPrinter, setTargetPrinter] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isOpen = isCreatePrinterModalOpen || isEditPrinterModalOpen;
  const isEditMode = isEditPrinterModalOpen;
  const { printer, onSuccess } =
    (isEditMode ? editPrinterModalData : createPrinterModalData) || {};
  const formRef = useRef(null);

  const closeModal = () =>
    isCreatePrinterModalOpen
      ? setIsCreatePrinterModalOpen(false)
      : setIsEditPrinterModalOpen(false);

  useEffect(() => {
    if (isOpen) {
      setTargetPrinter((isEditMode && printer) || {});
    } else if (!isSubmitting) {
      // reset modal on closing
      setTargetPrinter({});
    }
  }, [printer, isEditMode, isOpen]);

  const _closeModalSafely = () => closeModalSafely(isSubmitting, closeModal);

  const _handleSubmit = (event, errors, values) => {
    handleSubmit(
      event,
      errors,
      { ...values, buzzCount: parseInt(values.buzzCount || '0') }, // TODO: this should be handled by the Field itself!
      targetPrinter,
      isEditMode,
      setIsSubmitting,
      setTargetPrinter,
      closeModal,
      fetchPrinters,
    );
  };

  const handlePrintTest = () => {
    basePostQuery(
      'printers/' + targetPrinter?.id + '/test-print-job',
      {
        orderType: targetPrinter?.orderType,
      },
      null,
      'testPrintSuccess',
      'testPrintError',
    );
  };

  return (
    <ModalSheet
      isOpen={isOpen}
      closeModal={_closeModalSafely}
      title={intl.formatMessage({
        id: isEditMode ? 'editPrinterTitle' : 'createNewPrinterTitle',
      })}
      footer={
        <div className="align-content-center">
          <SaveSubmitButton
            data-testid="printerSaveButton"
            className="m-auto"
            disabled={isSubmitting}
            isSubmitting={isSubmitting}
            onClick={() => formRef?.current.handleSubmit()}
          />
        </div>
      }
    >
      <div className="px-1">
        <AvForm
          ref={formRef}
          onSubmit={_handleSubmit}
          disabled={isSubmitting}
          className="mt-1"
        >
          <Row>
            <Col xs={6} className="pr-50">
              <FormField
                data-testid="printerNameInput"
                propertyName="name"
                messageId="name"
                object={targetPrinter}
                required
              />
            </Col>
            <Col xs={6} className="pl-50">
              <FormField
                data-testid="printerCloudIdInput"
                propertyName="cloudId"
                messageId="cloudId"
                object={targetPrinter}
                disabled={isEditPrinterModalOpen}
                required
              />
            </Col>
          </Row>

          <Row>
            <Col xs={6} className="pr-50">
              <DropdownField
                isMulti={false}
                options={[
                  {
                    value: 'small',
                    label: intl.formatMessage({ id: 'smallFontType' }),
                  },
                  {
                    value: 'medium',
                    label: intl.formatMessage({ id: 'mediumFontType' }),
                  },
                  {
                    value: 'large',
                    label: intl.formatMessage({ id: 'largeFontType' }),
                  },
                ]}
                propertyName="fontSize"
                object={targetPrinter}
                placeholderMessageId="selectFontSizeLabel"
                labelMessageId="fontSizeTitle"
                required
              />
            </Col>
            <Col xs={6} className="pl-50">
              <FormField
                data-testid="buzzCountInput"
                propertyName="buzzCount"
                messageId="buzzCountLabel"
                object={targetPrinter}
              />
            </Col>
          </Row>

          <ItemCategoryField
            minMenuHeight={300}
            object={targetPrinter}
            options={itemCategories}
            setter={setTargetPrinter}
          />
          {isEditMode && (
            <>
              <hr />
              <Row>
                <Col className="pr-50 mt-1" xs={6}>
                  <DropdownField
                    isMulti={false}
                    options={[
                      ORDER_TYPE_INHOUSE,
                      ORDER_TYPE_PICKUP,
                      ORDER_TYPE_DELIVERY,
                      ORDER_TYPE_CATERING,
                      ORDER_TYPE_SELF_SERVICE,
                    ]}
                    propertyName="orderType"
                    object={targetPrinter}
                    labelMessageId="testPrintSelectOrderTypeLabel"
                    onChange={(orderType) => {
                      setTargetPrinter((prevState) => {
                        return {
                          ...prevState,
                          orderType,
                        };
                      });
                    }}
                  />
                </Col>
                <Col className="pl-50 mt-1" xs={6}>
                  <Button
                    color="primary"
                    className="w-100"
                    onClick={handlePrintTest}
                  >
                    <FormattedMessage id="testPrintSubmitLabel" />
                  </Button>
                </Col>
              </Row>
            </>
          )}
        </AvForm>
      </div>
    </ModalSheet>
  );
};

const mapStateToProps = (state) => ({
  isCreatePrinterModalOpen: state.modals.isCreatePrinterModalOpen,
  isEditPrinterModalOpen: state.modals.isEditPrinterModalOpen,
  createPrinterModalData: state.modals.createPrinterModalData,
  editPrinterModalData: state.modals.editPrinterModalData,
  itemCategories: state.store.itemCategories,
});

export default connect(mapStateToProps, {
  setIsCreatePrinterModalOpen,
  setIsEditPrinterModalOpen,
  fetchPrinters,
})(CreateOrEditPrinterModal);
